import { useCallback, useEffect, useState } from "react";
import Axios from "../config/axios";
import { UrlConstant } from "../commom/UrlConstant";

export interface StateOption {
  code: string; // ISO 3166-2 short code, e.g. "FL". This is what we send to the API as `state`.
  name: string; // display only
}

export interface CountryOption {
  alpha2: string;
  name: string;
  countryCode: string | null;
  isStateRequired: boolean;
}

const statesCache = new Map<string, StateOption[]>();
let supportedCache: CountryOption[] | null = null;

export const useSupportedCountries = (): CountryOption[] => {
  const [countries, setCountries] = useState<CountryOption[]>(supportedCache ?? []);

  useEffect(() => {
    if (supportedCache) return;
    let alive = true;
    Axios.get(UrlConstant.Common.Countries, { params: { supported: true } })
      .then((res) => {
        const list = res.data?.data;
        if (alive && Array.isArray(list) && list.length) {
          supportedCache = list.map((c: CountryOption) => ({
            alpha2: c.alpha2,
            name: c.name,
            countryCode: c.countryCode,
            isStateRequired: c.isStateRequired === true,
          }));
          setCountries(supportedCache as CountryOption[]);
        }
      })
      .catch(() => {
        /* keep the fallback list */
      });
    return () => {
      alive = false;
    };
  }, []);

  return countries;
};

const fetchStates = async (country: string): Promise<StateOption[]> => {
  try {
    const res = await Axios.get(UrlConstant.Common.CountryStates(country));
    const list = res.data?.data;
    if (!Array.isArray(list)) throw new Error("bad response");
    return list.map((s: { code: string; name: string }) => ({ code: s.code, name: s.name }));
  } catch (err) {
    // Legacy US-only endpoint as a safety net during rollout.
    if (country !== "US") throw err;
    const res = await Axios.get(UrlConstant.Common.StateList);
    const list = res.data?.data ?? res.data?.states ?? [];
    return list
      .map((s: { code?: string; abbreviation?: string; name: string }) => ({
        code: (s.code ?? s.abbreviation ?? "") as string,
        name: s.name,
      }))
      .filter((s: StateOption) => s.code);
  }
};

/** Loads the state list for a country. Pass null to skip. Results are cached per session. */
export const useCountryStates = (country: string | null) => {
  const [states, setStates] = useState<StateOption[]>(country ? statesCache.get(country) ?? [] : []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!country) {
      setStates([]);
      setError(false);
      return;
    }
    const cached = statesCache.get(country);
    if (cached) {
      setStates(cached);
      setError(false);
      return;
    }
    let alive = true; // ignore the response if the user switched country meanwhile
    setLoading(true);
    setError(false);
    fetchStates(country)
      .then((list) => {
        if (!alive) return;
        statesCache.set(country, list);
        setStates(list);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [country, attempt]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  return { states, loading, error, reload };
};
